// components/ITRManagement.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getYear } from 'date-fns';
import { ArrowRight, AlertCircle, AlertTriangle } from 'lucide-react';
import Form2307Form from './Form2307Form';
import ChartOfAccountsSearch from './ChartOfAccountsSearch';
import itrAPI from '../api/itrAPI';
import formAPI from '../api/formAPI';
import useAtcCodes from '../hooks/useAtcCodes';
import { cleanTIN, findMissingTINs, missingTINMessage } from '../utils/tinFormat';
import { resolveFtypeCode, isCorporateFiler, missingFtypeMessage } from '../utils/sawtFormType';
import { filerRdoCode, missingRdoMessage } from '../utils/rdoCode';
import { isCorporatePayee, findMissingPayeeEntities, missingPayeeEntityMessage } from '../utils/payeeEntity';
import { buildSawtXls, toSawtXlsRow, sawtXlsFilename, downloadSawtXls } from '../utils/sawtXlsExport';
import { buildSawtDat, downloadSawtDat } from '../utils/sawtDat';
import { datFilerBranch } from '../utils/branchCode';
import { useVendorTerms } from '../utils/terminology';

// ─── Constants ────────────────────────────────────────────────────────────────
const QUARTER_MONTHS = {
  1: [{ value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' }],
  2: [{ value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' }],
  3: [{ value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' }],
  4: [{ value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }],
};

// Flat list for the "All Quarters" case — derived from QUARTER_MONTHS so the
// two can never drift.
const ALL_MONTHS = [1, 2, 3, 4].flatMap((q) => QUARTER_MONTHS[q]);

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const INCOME_TYPES = ['Sales', 'Service Income', 'Rental Income', 'Interest Income', 'Other Income'];
const DEDUCTION_TYPES = [
  'Cost of Sales', 'Operating Expense', 'Depreciation',
  'Interest Expense', 'Other Deductions',
];

const COS_COMPONENTS = [
  { value: 'beginning_inventory', label: 'Beginning Inventory' },
  { value: 'purchases', label: 'Purchases' },
  { value: 'ending_inventory', label: 'Ending Inventory' },
];

const fmt = (n) =>
  new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(n || 0);

const fmtAmount = (n) =>
  (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const deriveQuarter = (month) => Math.ceil(parseInt(month) / 3);

// BIR TRAIN graduated income tax table (annual, in force 2023→present)
const computeGraduatedTax = (net) => {
  net = Math.max(0, net);
  if (net <= 250000)   return 0;
  if (net <= 400000)   return (net - 250000) * 0.15;
  if (net <= 800000)   return 22500  + (net - 400000)  * 0.20;
  if (net <= 2000000)  return 102500 + (net - 800000)  * 0.25;
  if (net <= 8000000)  return 402500 + (net - 2000000) * 0.30;
  return 2202500 + (net - 8000000) * 0.35;
};


// ─── Shared period selector ───────────────────────────────────────────────────
const PeriodSelector = ({ selectedPeriod, onChange, availableYears }) => {
  const { quarter, year, month } = selectedPeriod;

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label className="block text-xs text-gray-500 mb-1">Fiscal Year</label>
        <select
          value={year}
          onChange={(e) => onChange({ ...selectedPeriod, year: Number(e.target.value) })}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
        >
          {availableYears.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Quarter</label>
        <select
          value={quarter ?? ''}
          onChange={(e) =>
            onChange({ ...selectedPeriod, quarter: e.target.value ? Number(e.target.value) : null, month: null })
          }
          className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
        >
          <option value="">All Quarters</option>
          <option value={1}>Q1 (Jan–Mar)</option>
          <option value={2}>Q2 (Apr–Jun)</option>
          <option value={3}>Q3 (Jul–Sep)</option>
          <option value={4}>Q4 (Oct–Dec)</option>
        </select>
      </div>
      {/* Always shown. It used to render only once a quarter was picked, which
          made a month unreachable from the default "All Quarters" view — the
          period month could never be set, so month filtering looked broken and
          the SAWT Monthly XLS export stayed disabled. With no quarter selected
          the list is all twelve months; a quarter narrows it to its own. */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Month</label>
        <select
          value={month ?? ''}
          onChange={(e) => {
            const nextMonth = e.target.value ? Number(e.target.value) : null;
            onChange({
              ...selectedPeriod,
              month: nextMonth,
              // Keep the two in step: picking a month outside the selected
              // quarter would otherwise filter to nothing.
              quarter: nextMonth ? deriveQuarter(nextMonth) : quarter,
            });
          }}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
        >
          <option value="">All Months</option>
          {(quarter ? QUARTER_MONTHS[quarter] : ALL_MONTHS).map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>
      <button
        type="button"
        onClick={() => onChange({ quarter: null, year: new Date().getFullYear(), month: null })}
        className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-50"
      >
        Reset
      </button>
    </div>
  );
};

// ─── Small reusable action buttons ───────────────────────────────────────────
const EditBtn = ({ onClick }) => (
  <button type="button" title="Edit" onClick={onClick} className="text-teal-600 hover:text-teal-800">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a2 2 0 01-1.415.586H7v-3.414a2 2 0 01.586-1.414z" />
    </svg>
  </button>
);

const DeleteBtn = ({ onClick, disabled }) => (
  <button
    type="button" title="Delete" onClick={onClick} disabled={disabled}
    className="text-red-500 hover:text-red-700 disabled:opacity-40"
  >
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  </button>
);

const PaginationBar = ({ page, totalItems, limit, onPage }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-2 border-t bg-gray-50 text-xs text-gray-600">
      <span>{totalItems} record(s) — Page {page} of {totalPages}</span>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          className="px-2 py-1 border rounded disabled:opacity-40 hover:bg-gray-100"
        >
          Prev
        </button>
        <button
          type="button"
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages}
          className="px-2 py-1 border rounded disabled:opacity-40 hover:bg-gray-100"
        >
          Next
        </button>
      </div>
    </div>
  );
};

const FORM2307_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'received', label: 'Received' },
  { value: 'used_for_credit', label: 'Used for Credit' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
];

// ─── Inline-edit modal for income & deduction rows ───────────────────────────
const InlineEditModal = ({ type, record, fiscalYear, onSave, onClose, clientId = null }) => {
  const isIncome = type === 'income';
  const fy = fiscalYear ?? record?.fiscalYear;
  const initDate = record.transactionDate
    ? new Date(record.transactionDate).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    month: record.month ?? 1,
    description: record.description || '',
    chartOfAccountName: record.chartOfAccountName || '',
    chartOfAccount: record.chartOfAccount || null,
    incomeType: record.incomeType || 'Other Income',
    deductionType: record.deductionType || 'Other Deductions',
    cosComponent: record.cosComponent || '',
    amount: isIncome ? (record.grossAmount ?? '') : (record.amount ?? ''),
    orNumber: record.orNumber || '',
    transactionDate: initDate,
  });
  const [saving, setSaving] = useState(false);
  const [dateError, setDateError] = useState(() => {
    if (!isIncome || !fy) return '';
    const d = new Date(initDate);
    if (isNaN(d.getTime())) return '';
    const txYear = getYear(d);
    return txYear !== fy
      ? `Transaction Date year (${txYear}) does not match the selected Fiscal Year (${fy}). Please correct one or the other.`
      : '';
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isIncome && dateError) return;
    setSaving(true);
    try {
      const payload = {
        month: parseInt(form.month),
        description: form.description,
        chartOfAccount: form.chartOfAccount || undefined,
        chartOfAccountName: form.chartOfAccountName,
        orNumber: form.orNumber || undefined,
        transactionDate: form.transactionDate,
        ...(isIncome
          ? { incomeType: form.incomeType, grossAmount: parseFloat(form.amount) || 0 }
          : {
              deductionType: form.deductionType,
              amount: parseFloat(form.amount) || 0,
              ...(form.deductionType === 'Cost of Sales' && { cosComponent: form.cosComponent }),
            }),
      };
      await onSave(record._id, payload);
      onClose();
    } catch (err) {
      alert('Failed to save: ' + (err?.message || err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Edit {isIncome ? 'Income' : 'Deduction'} Record
        </h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Month</label>
              <select
                value={form.month}
                onChange={(e) => set('month', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
                required
              >
                {MONTH_NAMES.slice(1).map((name, idx) => (
                  <option key={idx + 1} value={idx + 1}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {isIncome ? 'Income Type' : 'Deduction Type'}
              </label>
              <select
                value={isIncome ? form.incomeType : form.deductionType}
                onChange={(e) => set(isIncome ? 'incomeType' : 'deductionType', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
              >
                {(isIncome ? INCOME_TYPES : DEDUCTION_TYPES).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          {!isIncome && form.deductionType === 'Cost of Sales' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Cost of Sales Component *</label>
              <select
                value={form.cosComponent}
                onChange={(e) => set('cosComponent', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
                required
              >
                <option value="">Select component</option>
                {COS_COMPONENTS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <ChartOfAccountsSearch
              value={form.chartOfAccountName || form.description}
              onChange={(v) => set('description', v)}
              onSelect={(acc) => {
                set('chartOfAccount', acc?._id || null);
                set('chartOfAccountName', acc ? `${acc.accountNumber} - ${acc.accountName}` : '');
                if (acc && !form.description) set('description', acc.accountName);
              }}
              placeholder="Description or search COA"
              showLinkedBadge={false}
              clientId={clientId}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {isIncome ? 'Gross Amount (₱)' : 'Amount (₱)'}
              </label>
              <input
                type="number" step="0.01" min="0"
                value={form.amount}
                onChange={(e) => set('amount', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Transaction Date</label>
              <input
                type="date"
                value={form.transactionDate}
                onChange={(e) => {
                  const val = e.target.value;
                  set('transactionDate', val);
                  if (isIncome && val && fy) {
                    const d = new Date(val);
                    const txYear = isNaN(d.getTime()) ? null : getYear(d);
                    setDateError(
                      txYear !== null && txYear !== fy
                        ? `Transaction Date year (${txYear}) does not match the selected Fiscal Year (${fy}). Please correct one or the other.`
                        : ''
                    );
                  }
                }}
                className={`w-full border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 ${
                  dateError ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500'
                }`}
              />
              {dateError && (
                <div className="flex items-start gap-1 mt-1">
                  <AlertCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-600">{dateError}</p>
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              OR Number <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <input
              type="text"
              value={form.orNumber}
              onChange={(e) => set('orNumber', e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
              placeholder="Official Receipt Number"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving || (isIncome && !!dateError)}
              className="px-4 py-2 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Edit modal for Form 2307 rows ───────────────────────────────────────────
const Form2307EditModal = ({ record, onSave, onClose, payeeClass }) => {
  // ATC list/rates come from the BIR reference table (see useAtcCodes).
  // Same rule as Form2307Form: the client is the payee on a 2307. The class is
  // passed in — this modal has no `client` prop of its own.
  const { options: atcOptions, byCode: atcByCode } = useAtcCodes('2307', { payeeClass });
  const [form, setForm] = useState({
    month:             record.month ?? 1,
    baseAmount:        record.baseAmount ?? '',
    withholdingRate:   record.withholdingRate ?? '',
    taxWithheld:       record.taxWithheld ?? '',
    atcCode:           record.atcCode || '',
    certificateNumber: record.certificateNumber || '',
    certificateDate:   record.certificateDate
      ? new Date(record.certificateDate).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10),
    transactionDate:   record.transactionDate
      ? new Date(record.transactionDate).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10),
    description: record.description || '',
    status:      record.status || 'received',
    notes:       record.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleBaseOrRate = (k, v) => {
    setForm((p) => {
      const next = { ...p, [k]: v };
      const base = parseFloat(next.baseAmount) || 0;
      const rate = parseFloat(next.withholdingRate) || 0;
      next.taxWithheld = ((base * rate) / 100).toFixed(2);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(record._id, {
        month:             parseInt(form.month),
        baseAmount:        parseFloat(form.baseAmount) || 0,
        withholdingRate:   parseFloat(form.withholdingRate) || 0,
        taxWithheld:       parseFloat(form.taxWithheld) || 0,
        atcCode:           form.atcCode || undefined,
        certificateNumber: form.certificateNumber,
        certificateDate:   form.certificateDate,
        transactionDate:   form.transactionDate,
        description:       form.description,
        status:            form.status,
        notes:             form.notes || undefined,
      });
      onClose();
    } catch (err) {
      alert('Failed to save: ' + (err?.message || err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 overflow-y-auto max-h-[90vh]">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Edit Form 2307 Record</h3>
        <p className="text-xs text-gray-500 mb-4">
          Vendor: <strong>{record.vendor?.name || record.payerName || '—'}</strong>
          {record.isPostedToGL && (
            <span className="ml-2 inline-block px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-xs font-medium">
              Posted to GL — saving will auto-reverse the entry
            </span>
          )}
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Month</label>
              <select
                value={form.month}
                onChange={(e) => set('month', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
                required
              >
                {MONTH_NAMES.slice(1).map((name, idx) => (
                  <option key={idx + 1} value={idx + 1}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => set('status', e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
              >
                {FORM2307_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text" value={form.description}
              onChange={(e) => set('description', e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Certificate Number</label>
              <input
                type="text" value={form.certificateNumber}
                onChange={(e) => set('certificateNumber', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Certificate Date</label>
              <input
                type="date" value={form.certificateDate}
                onChange={(e) => set('certificateDate', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Base Amount (₱)</label>
              <input
                type="number" step="0.01" min="0" value={form.baseAmount}
                onChange={(e) => handleBaseOrRate('baseAmount', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Rate (%)</label>
              <input
                type="number" step="0.01" min="0" max="100" value={form.withholdingRate}
                onChange={(e) => handleBaseOrRate('withholdingRate', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Tax Withheld (₱)</label>
              <input
                type="number" step="0.01" min="0" value={form.taxWithheld}
                onChange={(e) => set('taxWithheld', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">ATC Code</label>
              <select
                value={form.atcCode}
                onChange={(e) => {
                  const atcCode = e.target.value;
                  const known = atcByCode[atcCode];
                  set('atcCode', atcCode);
                  // Unknown code -> leave the stored rate alone; never guess one.
                  if (known) set('withholdingRate', String(known.ratePercent));
                }}
                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
              >
                {atcOptions.map((o) => (
                  <option key={o.value} value={o.value} title={o.description}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Transaction Date</label>
              <input
                type="date" value={form.transactionDate}
                onChange={(e) => set('transactionDate', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
            <input
              type="text" value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ITRManagement = ({ client }) => {
  const term = useVendorTerms();
  // On a 2307 the client is the payee, so the ATC list follows the client's own
  // entity type. Used for the edit modal's dropdown and for the XLS export's
  // NATURE OF PAYMENT descriptions.
  const clientPayeeClass = isCorporateFiler(client?.clientType) ? 'corporation' : 'individual';
  // UNFILTERED on purpose. The payee-class narrowing above is for the entry
  // dropdown; as a LOOKUP table it would silently fail to resolve any code from
  // the other class, and real records carry both (WI* and WC*). A lookup must
  // cover every code that can appear on a record, not just the offerable ones.
  const { byCode: atcByCode } = useAtcCodes('2307');
  const currentYear = new Date().getFullYear();
  const isCorpClient = client?.clientType === 'corporation';
  const availableYears = useMemo(
    () => Array.from({ length: 6 }, (_, i) => currentYear - i),
    [currentYear]
  );

  const [activeSection, setActiveSection] = useState('income');

  const [incomeRecords, setIncomeRecords] = useState([]);
  const [deductionRecords, setDeductionRecords] = useState([]);
  const [form2307Records, setForm2307Records] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedPeriod, setSelectedPeriod] = useState({
    quarter: null,
    year: currentYear,
    month: null,
  });

  // ITR Tax Due (manual, stored in ITRAdjustment)
  const [itrTaxDue, setItrTaxDue] = useState(0);
  const [savingTaxDue, setSavingTaxDue] = useState(false);

  // Tax computation fields
  const [itrMethod, setItrMethod] = useState('Graduated (Itemized)');
  const [ritRate, setRitRate] = useState(0.25);
  const [subjectToMCIT, setSubjectToMCIT] = useState(false);
  const [computedRIT, setComputedRIT] = useState(0);
  const [computedMCIT, setComputedMCIT] = useState(0);

  // Edit state
  const [editingIncome, setEditingIncome] = useState(null);
  const [editingDeduction, setEditingDeduction] = useState(null);
  const [editingForm2307, setEditingForm2307] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [recentIncomeTotal, setRecentIncomeTotal] = useState(0);
  const [recentDeductionTotal, setRecentDeductionTotal] = useState(0);
  const [incomeDateError, setIncomeDateError] = useState('');
  const [deductionDateError, setDeductionDateError] = useState('');

  // Encode forms state
  const [incomeForm, setIncomeForm] = useState({
    month: new Date().getMonth() + 1,
    description: '', chartOfAccount: null, chartOfAccountName: '',
    incomeType: 'Other Income', grossAmount: '', orNumber: '',
    transactionDate: new Date().toISOString().slice(0, 10),
  });
  const [deductionForm, setDeductionForm] = useState({
    month: new Date().getMonth() + 1,
    description: '', chartOfAccount: null, chartOfAccountName: '',
    deductionType: 'Other Deductions', cosComponent: '', amount: '', orNumber: '',
    transactionDate: new Date().toISOString().slice(0, 10),
  });
  const [incomeFormKey, setIncomeFormKey] = useState(0);
  const [deductionFormKey, setDeductionFormKey] = useState(0);
  const [submittingIncome, setSubmittingIncome] = useState(false);
  const [submittingDeduction, setSubmittingDeduction] = useState(false);

  // Corp: all-year data for carry-over + payments
  const [allYearIncomeRecords, setAllYearIncomeRecords] = useState([]);
  const [allYearDeductionRecords, setAllYearDeductionRecords] = useState([]);
  const [allYearForm2307Records, setAllYearForm2307Records] = useState([]);
  const [allYearPayments, setAllYearPayments] = useState([]);
  const [itrPayments, setItrPayments] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    paymentDate: new Date().toISOString().slice(0, 10),
    amountPaid: '',
    referenceNumber: '',
    remarks: '',
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);

  // Records tab search / filter / pagination
  const [recordsSearch, setRecordsSearch] = useState('');
  const [recordsAccountId, setRecordsAccountId] = useState('');
  const [recordsDateFrom, setRecordsDateFrom] = useState('');
  const [recordsDateTo, setRecordsDateTo] = useState('');
  const [recordsTypeFilter, setRecordsTypeFilter] = useState('all'); // 'all' | 'income' | 'deduction' | 'form2307'
  const [recordsIncPage, setRecordsIncPage] = useState(1);
  const [recordsDedPage, setRecordsDedPage] = useState(1);
  const [records2307Page, setRecords2307Page] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // ── Fetch helpers ─────────────────────────────────────────────────────────
  const buildParams = useCallback(() => {
    const p = { fiscalYear: selectedPeriod.year, limit: 50, sort: 'transactionDate', order: 'desc', page: 1 };
    if (selectedPeriod.quarter) p.quarter = selectedPeriod.quarter;
    if (selectedPeriod.month) p.month = selectedPeriod.month;
    return p;
  }, [selectedPeriod]);

  const fetchIncomeRecords = useCallback(async () => {
    if (!client?._id) return;
    try {
      const res = await itrAPI.getIncome(client._id, buildParams());
      const data = res?.data?.data?.records || res?.data?.records || [];
      const total = res?.data?.data?.total ?? 0;
      setIncomeRecords(Array.isArray(data) ? data : []);
      setRecentIncomeTotal(total);
    } catch (err) {
      console.error('ITR income fetch error:', err);
      setIncomeRecords([]);
      setRecentIncomeTotal(0);
    }
  }, [client?._id, buildParams]);

  const fetchDeductionRecords = useCallback(async () => {
    if (!client?._id) return;
    try {
      const res = await itrAPI.getDeductions(client._id, buildParams());
      const data = res?.data?.data?.records || res?.data?.records || [];
      const total = res?.data?.data?.total ?? 0;
      setDeductionRecords(Array.isArray(data) ? data : []);
      setRecentDeductionTotal(total);
    } catch (err) {
      console.error('ITR deduction fetch error:', err);
      setDeductionRecords([]);
      setRecentDeductionTotal(0);
    }
  }, [client?._id, buildParams]);

  const fetchForm2307Records = useCallback(async () => {
    if (!client?._id) return;
    try {
      const params = { fiscalYear: selectedPeriod.year, limit: 500, page: 1 };
      if (selectedPeriod.quarter) params.quarter = selectedPeriod.quarter;
      if (selectedPeriod.month) params.month = selectedPeriod.month;
      const res = await formAPI.getForm2307ByClient(client._id, params);
      const data = res?.data?.data?.records || res?.data?.records || [];
      setForm2307Records(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('ITR 2307 fetch error:', err);
      setForm2307Records([]);
    }
  }, [client?._id, selectedPeriod]);

  const fetchAdjustment = useCallback(async () => {
    if (!client?._id || !selectedPeriod.quarter || !selectedPeriod.year) return;
    try {
      const res = await itrAPI.getAdjustment(client._id, {
        fiscalYear: selectedPeriod.year,
        quarter: selectedPeriod.quarter,
      });
      const data = res?.data?.data || {};
      setItrTaxDue(Number(data.itrTaxDue) || 0);
      setItrMethod(data.itrMethod || 'Graduated (Itemized)');
      setRitRate(data.ritRate || 0.25);
      setSubjectToMCIT(data.subjectToMCIT || false);
      setComputedRIT(Number(data.computedRIT) || 0);
      setComputedMCIT(Number(data.computedMCIT) || 0);
    } catch {
      setItrTaxDue(0);
      setItrMethod('Graduated (Itemized)');
      setRitRate(0.25);
      setSubjectToMCIT(false);
      setComputedRIT(0);
      setComputedMCIT(0);
    }
  }, [client?._id, selectedPeriod.quarter, selectedPeriod.year]);

  const fetchAllYearData = useCallback(async () => {
    if (!client?._id) return;
    const yearParams = { fiscalYear: selectedPeriod.year, limit: 1000, page: 1 };
    try {
      const [incRes, dedRes, ewtRes] = await Promise.all([
        itrAPI.getIncome(client._id, yearParams),
        itrAPI.getDeductions(client._id, yearParams),
        formAPI.getForm2307ByClient(client._id, yearParams),
      ]);
      const extract = (res) => {
        const d = res?.data?.data?.records || res?.data?.records || [];
        return Array.isArray(d) ? d : [];
      };
      setAllYearIncomeRecords(extract(incRes));
      setAllYearDeductionRecords(extract(dedRes));
      setAllYearForm2307Records(extract(ewtRes));
    } catch (err) {
      console.error('ITR all-year fetch error:', err);
    }
  }, [client?._id, selectedPeriod.year]);

  const fetchPayments = useCallback(async () => {
    if (!client?._id) return;
    try {
      const res = await itrAPI.getPayments(client._id, {
        fiscalYear: selectedPeriod.year,
        limit: 100,
        page: 1,
      });
      const data = res?.data?.data?.records || res?.data?.records || [];
      const arr = Array.isArray(data) ? data : [];
      setAllYearPayments(arr);
      setItrPayments(arr.filter((p) => Number(p.quarter) === selectedPeriod.quarter));
    } catch (err) {
      console.error('ITR payments fetch error:', err);
      setAllYearPayments([]);
      setItrPayments([]);
    }
  }, [client?._id, selectedPeriod.year, selectedPeriod.quarter]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchIncomeRecords(),
      fetchDeductionRecords(),
      fetchForm2307Records(),
      fetchAdjustment(),
      fetchAllYearData(),
      fetchPayments(),
    ]);
    setLoading(false);
  }, [fetchIncomeRecords, fetchDeductionRecords, fetchForm2307Records, fetchAdjustment, fetchAllYearData, fetchPayments]);

  const reloadAll = useCallback(async () => {
    await Promise.all([fetchIncomeRecords(), fetchDeductionRecords(), fetchAllYearData()]);
  }, [fetchIncomeRecords, fetchDeductionRecords, fetchAllYearData]);

  const autoPostNewITRRecordToGL = useCallback(async (record, kind) => {
    const recordId = record?._id;
    const accountingClientId = record?.accountingClient || client?._id;
    if (!recordId || !accountingClientId || !client?._id) {
      console.warn('[ITR auto-post] missing ids', { recordId, accountingClientId, clientId: client?._id });
      return false;
    }
    try {
      await itrAPI.postRecordToGL(client._id, { recordId, recordKind: kind, accountingClientId });
      return true;
    } catch (err) {
      const code = err?.response?.data?.code;
      const msg = err?.response?.data?.message || err?.message || 'GL posting failed';
      if (code === 'COA_NOT_BOUND') {
        alert(`GL posting skipped: ${msg}`);
      }
      console.error('Auto-post ITR record to GL failed:', err?.response?.data || err?.message || err);
      return false;
    }
  }, [client?._id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Debounce the Records-tab search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(recordsSearch), 300);
    return () => clearTimeout(t);
  }, [recordsSearch]);

  // Reset Records tab pages when period or filters change
  useEffect(() => {
    setRecordsIncPage(1);
    setRecordsDedPage(1);
    setRecords2307Page(1);
  }, [selectedPeriod, debouncedSearch, recordsAccountId, recordsTypeFilter, recordsDateFrom, recordsDateTo]);

  // Sync form month to selected period so new records always appear in current view
  useEffect(() => {
    const targetMonth = selectedPeriod.month
      || (selectedPeriod.quarter ? (selectedPeriod.quarter - 1) * 3 + 1 : new Date().getMonth() + 1);
    setIncomeForm((p) => ({ ...p, month: targetMonth }));
    setDeductionForm((p) => ({ ...p, month: targetMonth }));
  }, [selectedPeriod.quarter, selectedPeriod.month]);

  // Re-validate income transaction date when fiscal year changes
  useEffect(() => {
    if (!incomeForm.transactionDate) return;
    const d = new Date(incomeForm.transactionDate);
    if (isNaN(d.getTime())) return;
    const txYear = getYear(d);
    setIncomeDateError(
      txYear !== selectedPeriod.year
        ? `Transaction Date year (${txYear}) does not match the selected Fiscal Year (${selectedPeriod.year}). Please correct one or the other.`
        : ''
    );
  }, [selectedPeriod.year, incomeForm.transactionDate]);

  // Re-validate deduction transaction date when fiscal year changes
  useEffect(() => {
    if (!deductionForm.transactionDate) return;
    const d = new Date(deductionForm.transactionDate);
    if (isNaN(d.getTime())) return;
    const txYear = getYear(d);
    setDeductionDateError(
      txYear !== selectedPeriod.year
        ? `Transaction Date year (${txYear}) does not match the selected Fiscal Year (${selectedPeriod.year}). Please correct one or the other.`
        : ''
    );
  }, [selectedPeriod.year, deductionForm.transactionDate]);

  // ── Save ITR Tax Due ──────────────────────────────────────────────────────
  const saveItrTaxDue = async () => {
    if (!client?._id) return;
    const q = selectedPeriod.quarter;
    const fy = selectedPeriod.year;
    if (!q || !fy) {
      alert('Please select a quarter and year before saving ITR Tax Due.');
      return;
    }
    try {
      setSavingTaxDue(true);
      if (isCorpClient) {
        const rit = Math.max(0, summaryData.taxableIncome) * ritRate;
        const mcit = subjectToMCIT ? Math.max(0, summaryData.grossIncome) * 0.02 : 0;
        const computed = Math.max(rit, mcit);
        setComputedRIT(rit);
        setComputedMCIT(mcit);
        await itrAPI.saveAdjustment(client._id, {
          fiscalYear: fy, quarter: q,
          itrTaxDue: computed, ritRate, subjectToMCIT,
          computedRIT: rit, computedMCIT: mcit,
        });
        setItrTaxDue(computed);
      } else {
        const indivTax = itrMethod === '8% Flat Rate'
          ? Math.max(0, summaryData.totalIncome - 250000) * 0.08
          : computeGraduatedTax(summaryData.taxableIncome);
        await itrAPI.saveAdjustment(client._id, {
          fiscalYear: fy, quarter: q, itrTaxDue: indivTax, itrMethod,
        });
        setItrTaxDue(indivTax);
      }
    } catch (err) {
      alert('Failed to save: ' + (err?.message || err));
    } finally {
      setSavingTaxDue(false);
    }
  };

  // ── Record payment (Corp only) ────────────────────────────────────────────
  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!client?._id || !selectedPeriod.quarter || !selectedPeriod.year) {
      alert('Please select a quarter and year.');
      return;
    }
    try {
      setSubmittingPayment(true);
      await itrAPI.recordPayment(client._id, {
        fiscalYear: selectedPeriod.year,
        quarter: selectedPeriod.quarter,
        paymentDate: paymentForm.paymentDate,
        amountPaid: parseFloat(paymentForm.amountPaid) || 0,
        referenceNumber: paymentForm.referenceNumber,
        remarks: paymentForm.remarks,
      });
      setPaymentForm({
        paymentDate: new Date().toISOString().slice(0, 10),
        amountPaid: '', referenceNumber: '', remarks: '',
      });
      setShowPaymentForm(false);
      await fetchPayments();
    } catch (err) {
      alert('Failed to record payment: ' + (err?.message || err));
    } finally {
      setSubmittingPayment(false);
    }
  };

  // ── Summary computation (local, from fetched records) ────────────────────
  const filteredByPeriod = useCallback(
    (arr, monthField = 'month') =>
      arr.filter((r) => {
        if (selectedPeriod.quarter) {
          const q = deriveQuarter(r[monthField]);
          if (q !== selectedPeriod.quarter) return false;
        }
        if (selectedPeriod.month && r[monthField] !== selectedPeriod.month) return false;
        return true;
      }),
    [selectedPeriod]
  );

  const summaryData = useMemo(() => {
    const inc = filteredByPeriod(allYearIncomeRecords);
    const ded = filteredByPeriod(deductionRecords);
    const ewt = filteredByPeriod(form2307Records);

    const totalIncome = inc.reduce((s, r) => s + (Number(r.grossAmount) || 0), 0);

    // COGS calculation: beginning_inventory + purchases - ending_inventory
    const cogsRecords = ded.filter((r) => r.deductionType === 'Cost of Sales');
    let cogs = 0;
    cogsRecords.forEach((r) => {
      if (r.cosComponent === 'beginning_inventory' || r.cosComponent === 'purchases') {
        cogs += Number(r.amount) || 0;
      } else if (r.cosComponent === 'ending_inventory') {
        cogs -= Number(r.amount) || 0;
      }
    });
    cogs = Math.max(0, cogs);

    const grossIncome = totalIncome - cogs;

    // Allowable deductions (exclude Cost of Sales)
    const allowableDeductions = ded
      .filter((r) => r.deductionType !== 'Cost of Sales')
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);

    // No floor — graduated function floors internally; 8% uses totalIncome
    const taxableIncome = grossIncome - allowableDeductions;

    // Individual: live computed tax due (shown read-only, not from saved state)
    let computedIndivTaxDue = 0;
    if (!isCorpClient) {
      computedIndivTaxDue = itrMethod === '8% Flat Rate'
        ? Math.max(0, totalIncome - 250000) * 0.08
        : computeGraduatedTax(taxableIncome);
    }

    const total2307Credits = ewt.reduce((s, r) => s + (Number(r.taxWithheld) || 0), 0);
    const rawNet = itrTaxDue - total2307Credits;
    const netITRPayable = Math.max(0, rawNet);
    const excessCredit = rawNet < 0 ? Math.abs(rawNet) : 0;

    return {
      totalIncome,
      cogs,
      grossIncome,
      allowableDeductions,
      taxableIncome,
      computedIndivTaxDue,
      total2307Credits,
      netITRPayable,
      excessCredit,
      incomeCount: inc.length,
      deductionCount: ded.length,
      form2307Count: ewt.length,
    };
  }, [allYearIncomeRecords, deductionRecords, form2307Records, itrTaxDue, filteredByPeriod, isCorpClient, itrMethod]);

  // ── Cumulative carry-over (Q2/Q3/Q4 for both Corp and Individual) ────────
  const cumulativeData = useMemo(() => {
    if (!selectedPeriod.quarter || selectedPeriod.quarter === 1) return null;
    const currentQ = selectedPeriod.quarter;
    const incAll = allYearIncomeRecords.filter((r) => Number(r.quarter) <= currentQ);
    const dedAll = allYearDeductionRecords.filter((r) => Number(r.quarter) <= currentQ);
    const ewtAll = allYearForm2307Records.filter((r) => Number(r.quarter) <= currentQ);

    const cumTotalIncome = incAll.reduce((s, r) => s + (Number(r.grossAmount) || 0), 0);
    let cumCogs = 0;
    dedAll.filter((r) => r.deductionType === 'Cost of Sales').forEach((r) => {
      if (r.cosComponent === 'beginning_inventory' || r.cosComponent === 'purchases') cumCogs += Number(r.amount) || 0;
      else if (r.cosComponent === 'ending_inventory') cumCogs -= Number(r.amount) || 0;
    });
    cumCogs = Math.max(0, cumCogs);
    const cumGrossIncome = cumTotalIncome - cumCogs;
    const cumAllowableDeductions = dedAll
      .filter((r) => r.deductionType !== 'Cost of Sales')
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const cumTaxableIncome = cumGrossIncome - cumAllowableDeductions;

    let cumItrTaxDue;
    if (isCorpClient) {
      const cumRIT = Math.max(0, cumTaxableIncome) * ritRate;
      const cumMCIT = subjectToMCIT ? Math.max(0, cumGrossIncome) * 0.02 : 0;
      cumItrTaxDue = Math.max(cumRIT, cumMCIT);
    } else {
      // Individual: always run graduated table on cumulative taxable income (never sum quarters)
      cumItrTaxDue = itrMethod === '8% Flat Rate'
        ? Math.max(0, cumTotalIncome - 250000) * 0.08   // 250K deducted once per year
        : computeGraduatedTax(cumTaxableIncome);
    }

    const cumEWTCredits = ewtAll.reduce((s, r) => s + (Number(r.taxWithheld) || 0), 0);
    const priorPaid = allYearPayments
      .filter((p) => Number(p.quarter) < currentQ)
      .reduce((s, p) => s + (Number(p.amountPaid) || 0), 0);
    const cumNetITRPayable = cumItrTaxDue - cumEWTCredits - priorPaid;
    return { cumTotalIncome, cumCogs, cumGrossIncome, cumAllowableDeductions, cumTaxableIncome, cumItrTaxDue, cumEWTCredits, priorPaid, cumNetITRPayable };
  }, [selectedPeriod.quarter, allYearIncomeRecords, allYearDeductionRecords, allYearForm2307Records, allYearPayments, isCorpClient, ritRate, subjectToMCIT, itrMethod]);

  // ── Payment status ───────────────────────────────────────────────────────
  const paymentStatus = useMemo(() => {
    if (!selectedPeriod.quarter) return null;
    const totalPaid = itrPayments.reduce((s, p) => s + (Number(p.amountPaid) || 0), 0);
    const due = summaryData.netITRPayable;
    if (due <= 0) return { label: 'No Tax Due', color: 'gray', totalPaid };
    if (totalPaid >= due) return { label: 'Paid', color: 'green', totalPaid };
    if (totalPaid > 0) return { label: 'Partially Paid', color: 'yellow', totalPaid };
    return { label: 'Unpaid', color: 'red', totalPaid };
  }, [selectedPeriod.quarter, itrPayments, summaryData.netITRPayable]);

  // ── Income encode submit ───────────────────────────────────────────────────
  const handleIncomeSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPeriod.year) { alert('Please select a fiscal year.'); return; }
    if (incomeDateError) return;
    const desc = incomeForm.description?.trim() || incomeForm.chartOfAccountName?.trim();
    if (!desc) { alert('Description is required.'); return; }
    if (!incomeForm.chartOfAccount) {
      alert('Description must be linked to a Chart of Accounts entry. Please select an account from the search dropdown.');
      return;
    }
    if (!(parseFloat(incomeForm.grossAmount) > 0)) { alert('Gross Amount must be greater than 0.'); return; }
    try {
      setSubmittingIncome(true);
      const response = await itrAPI.createIncome(client._id, {
        fiscalYear: selectedPeriod.year,
        month: parseInt(incomeForm.month),
        description: desc,
        chartOfAccount: incomeForm.chartOfAccount || undefined,
        chartOfAccountName: incomeForm.chartOfAccountName,
        incomeType: incomeForm.incomeType,
        grossAmount: parseFloat(incomeForm.grossAmount),
        orNumber: incomeForm.orNumber || undefined,
        transactionDate: incomeForm.transactionDate,
      });
      const newRecord = response?.data?.data;
      setIncomeForm((p) => ({
        ...p,
        description: '', chartOfAccount: null, chartOfAccountName: '', grossAmount: '', orNumber: '',
        transactionDate: new Date().toISOString().slice(0, 10),
      }));
      setIncomeFormKey((k) => k + 1);
      if (newRecord) {
        const posted = await autoPostNewITRRecordToGL(newRecord, 'income');
        if (posted) { await reloadAll(); return; }
      }
      await fetchIncomeRecords();
      await fetchAllYearData();
    } catch (err) {
      alert('Failed: ' + (err?.response?.data?.message || err?.message || err));
    } finally {
      setSubmittingIncome(false);
    }
  };

  // ── Deduction encode submit ────────────────────────────────────────────────
  const handleDeductionSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPeriod.year) { alert('Please select a fiscal year.'); return; }
    const desc = deductionForm.description?.trim() || deductionForm.chartOfAccountName?.trim();
    if (!desc) { alert('Description is required.'); return; }
    if (!deductionForm.chartOfAccount) {
      alert('Description must be linked to a Chart of Accounts entry. Please select an account from the search dropdown.');
      return;
    }
    if (!(parseFloat(deductionForm.amount) > 0)) { alert('Amount must be greater than 0.'); return; }
    if (deductionForm.deductionType === 'Cost of Sales' && !deductionForm.cosComponent) {
      alert('Please select a Cost of Sales component (Beginning Inventory, Purchases, or Ending Inventory).');
      return;
    }
    try {
      setSubmittingDeduction(true);
      const payload = {
        fiscalYear: selectedPeriod.year,
        month: parseInt(deductionForm.month),
        description: desc,
        chartOfAccount: deductionForm.chartOfAccount || undefined,
        chartOfAccountName: deductionForm.chartOfAccountName,
        deductionType: deductionForm.deductionType,
        amount: parseFloat(deductionForm.amount),
        orNumber: deductionForm.orNumber || undefined,
        transactionDate: deductionForm.transactionDate,
        cosComponent: deductionForm.deductionType === 'Cost of Sales' ? deductionForm.cosComponent : null,
      };
      const response = await itrAPI.createDeduction(client._id, payload);
      const newRecord = response?.data?.data;
      // Reset fully — deductionType back to default prevents stale cosComponent requirement
      setDeductionForm((p) => ({
        ...p,
        description: '', chartOfAccount: null, chartOfAccountName: '', amount: '',
        deductionType: 'Other Deductions', cosComponent: '', orNumber: '',
        transactionDate: new Date().toISOString().slice(0, 10),
      }));
      setDeductionFormKey((k) => k + 1);
      if (newRecord) {
        const posted = await autoPostNewITRRecordToGL(newRecord, 'deduction');
        if (posted) { await reloadAll(); return; }
      }
      await fetchDeductionRecords();
      await fetchAllYearData();
    } catch (err) {
      alert('Failed: ' + (err?.response?.data?.message || err?.message || err));
    } finally {
      setSubmittingDeduction(false);
    }
  };

  // ── Delete handlers ───────────────────────────────────────────────────────
  const handleDeleteIncome = async (id) => {
    if (!window.confirm('Delete this income record?')) return;
    try {
      setDeletingId(id);
      await itrAPI.deleteIncome(id);
      await fetchIncomeRecords();
      await fetchAllYearData();
    } catch (err) { alert('Failed: ' + (err?.message || err)); }
    finally { setDeletingId(null); }
  };

  const handleDeleteDeduction = async (id) => {
    if (!window.confirm('Delete this deduction record?')) return;
    try {
      setDeletingId(id);
      await itrAPI.deleteDeduction(id);
      await fetchDeductionRecords();
      await fetchAllYearData();
    } catch (err) { alert('Failed: ' + (err?.message || err)); }
    finally { setDeletingId(null); }
  };

  const handleDeleteForm2307 = async (id) => {
    if (!window.confirm('Delete this Form 2307 record? Any GL posting will be automatically reversed.')) return;
    try {
      setDeletingId(id);
      await formAPI.deleteForm2307Record(id);
      await fetchForm2307Records();
      await fetchAllYearData();
    } catch (err) { alert('Failed: ' + (err?.message || err)); }
    finally { setDeletingId(null); }
  };

  // ── Export SAWT monthly summary (XLS) ─────────────────────────────────────
  // Additional format alongside the DAT export — same Form2307Record source,
  // same period scoping. Does not touch exportDAT.
  const exportSawtXls = useCallback(() => {
    const month = selectedPeriod.month;
    if (!month) {
      alert('Select a month in the period filter — this summary is a monthly report.');
      return;
    }
    const year = selectedPeriod.year || new Date().getFullYear();

    const monthRecords = form2307Records.filter(
      (r) => r.month === month && (!selectedPeriod.year || r.fiscalYear === selectedPeriod.year)
    );
    if (!monthRecords.length) {
      alert(`No Form 2307 records for ${month}/${year}.`);
      return;
    }

    const clientTIN = cleanTIN(client?.tin);
    if (!clientTIN) {
      alert(missingFtypeMessage(client?.name));
      return;
    }
    const ftypeCode = resolveFtypeCode(client?.clientType);
    if (!ftypeCode) {
      alert(missingFtypeMessage(client?.name));
      return;
    }

    // Local on purpose: exportDAT has its own copy and this pass must not
    // touch the DAT export.
    const payees = monthRecords.map((r) => ({
      name: r.vendor?.name || r.payerName || r.vendorName || '',
      tin: r.vendor?.tin || r.payerTin || '',
      businessType: r.vendor?.businessType || r.payerType,
    }));

    // Payee TIN must be real — same rule as the DAT export.
    const missingTINs = findMissingTINs(payees);
    if (missingTINs.length) {
      alert(missingTINMessage(missingTINs));
      return;
    }

    // A payee whose entity type is unknown cannot be placed in either name
    // column. Flag it rather than defaulting — a wrong column is a data error.
    const missingEntities = findMissingPayeeEntities(payees);
    if (missingEntities.length) {
      alert(missingPayeeEntityMessage(missingEntities, term.lower));
      return;
    }

    // NATURE OF PAYMENT is a required column and comes from the ATC table. A
    // code that is missing, malformed or retired resolves to nothing — ship
    // that and the filing carries blank cells. Name the codes instead.
    const unresolved = [...new Set(
      monthRecords
        .filter((r) => !atcByCode[r.atcCode]?.description)
        .map((r) => r.atcCode || '(no ATC code)')
    )];
    if (unresolved.length) {
      alert(
        `These ATC codes are not in the reference table, so NATURE OF PAYMENT cannot be filled: `
        + `${unresolved.join(', ')}. Correct the records, or add the codes at `
        + `Admin \u2192 ATC Codes, then export again.`
      );
      return;
    }

    const rows = monthRecords.map((r) =>
      toSawtXlsRow(r, { atcDescription: atcByCode[r.atcCode].description })
    );

    const xml = buildSawtXls({ client, formCode: ftypeCode, month, year, rows });
    downloadSawtXls(xml, sawtXlsFilename({ client, month, year }));
  }, [form2307Records, selectedPeriod, client, atcByCode]);

  // ── Export BIR DAT file ───────────────────────────────────────────────────
  const exportDAT = useCallback(() => {
    const recordsToExport = form2307Records.filter((r) => {
      if (selectedPeriod.quarter) {
        const q = r.month <= 3 ? 1 : r.month <= 6 ? 2 : r.month <= 9 ? 3 : 4;
        if (q !== selectedPeriod.quarter) return false;
      }
      if (selectedPeriod.year && r.fiscalYear !== selectedPeriod.year) return false;
      return true;
    });

    if (!recordsToExport.length) {
      alert('No Form 2307 records for the selected period.');
      return;
    }
    const clientTIN9 = cleanTIN(client?.tin);
    if (!clientTIN9) {
      alert('Client TIN is required. Please update the client profile.');
      return;
    }

    // A missing payee TIN must block the file, never be padded into 000000000.
    const payeeOf = (r) => ({
      name: r.vendor?.name || r.payerName || r.vendorName || '',
      tin: r.vendor?.tin || r.payerTin || '',
    });
    const missingTINs = findMissingTINs(recordsToExport.map(payeeOf));
    if (missingTINs.length) {
      alert(missingTINMessage(missingTINs));
      return;
    }

    // FTYPE_CODE is the filer's entity type (1701/1702), never a return code.
    const ftypeCode = resolveFtypeCode(client?.clientType);
    if (!ftypeCode) {
      alert(missingFtypeMessage(client?.name));
      return;
    }

    const rdoCode = filerRdoCode(client);
    if (!rdoCode) {
      alert(missingRdoMessage(client?.name));
      return;
    }

    // Period MM/YYYY — passed through as selected (period logic is a separate task).
    const now = new Date();
    let periodMonth;
    if (selectedPeriod.month) {
      periodMonth = selectedPeriod.month;
    } else if (selectedPeriod.quarter) {
      periodMonth = selectedPeriod.quarter * 3;
    } else {
      periodMonth = now.getMonth() + 1;
    }
    const period = { month: periodMonth, year: selectedPeriod.year || now.getFullYear() };

    const result = buildSawtDat({
      filer: {
        tin: client?.tin,
        branchCode: datFilerBranch(client?.branchCode),
        rdoCode,
        clientType: client?.clientType,
        name: client?.name,
      },
      period,
      rows: recordsToExport.map((r) => {
        const payee = payeeOf(r);
        return {
          tin: payee.tin,
          branchCode: '0000',
          payeeEntity: isCorporatePayee(r.vendor?.businessType || r.payerType) ? 'corporation' : 'individual',
          name: payee.name,
          period,
          atcCode: r.atcCode,
          ratePercent: r.withholdingRate,
          baseAmount: r.baseAmount,
          taxWithheld: r.taxWithheld,
        };
      }),
    });
    if (!result.ok) {
      alert(result.errors.join('\n'));
      return;
    }
    downloadSawtDat(result);
  }, [form2307Records, selectedPeriod, client]);

  // ── Shared table helpers ──────────────────────────────────────────────────
  const Th = ({ children, right }) => (
    <th className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide ${right ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );
  const Td = ({ children, right, mono }) => (
    <td className={`px-3 py-2 ${right ? 'text-right' : ''} ${mono ? 'font-mono text-xs' : ''}`}>
      {children}
    </td>
  );

  const byDateDesc = (a, b) =>
    new Date(b.transactionDate || 0) - new Date(a.transactionDate || 0);

  const incomeFiltered = useMemo(
    () => filteredByPeriod(incomeRecords).sort(byDateDesc),
    [filteredByPeriod, incomeRecords]
  );
  const deductionFiltered = useMemo(
    () => filteredByPeriod(deductionRecords).sort(byDateDesc),
    [filteredByPeriod, deductionRecords]
  );
  const form2307Filtered = useMemo(
    () => filteredByPeriod(form2307Records).sort(byDateDesc),
    [filteredByPeriod, form2307Records]
  );
  const allIncomeFiltered = useMemo(
    () => filteredByPeriod(allYearIncomeRecords).sort(byDateDesc),
    [filteredByPeriod, allYearIncomeRecords]
  );

  // ── Records tab constants + computed data ─────────────────────────────────
  const RECORDS_LIMIT = 50;

  const recordsAccountOptions = useMemo(() => {
    const map = new Map();
    [...allYearIncomeRecords, ...allYearDeductionRecords].forEach((r) => {
      if (r.chartOfAccount && r.chartOfAccountName) {
        const id = typeof r.chartOfAccount === 'object'
          ? (r.chartOfAccount._id?.toString() || r.chartOfAccount.toString())
          : r.chartOfAccount.toString();
        if (id) map.set(id, r.chartOfAccountName);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allYearIncomeRecords, allYearDeductionRecords]);

  const recordsIncFiltered = useMemo(() => {
    let arr = filteredByPeriod(allYearIncomeRecords).sort(byDateDesc);
    if (debouncedSearch) {
      const s = debouncedSearch.toLowerCase();
      arr = arr.filter((r) => (r.description || '').toLowerCase().includes(s));
    }
    if (recordsAccountId) {
      arr = arr.filter((r) => {
        const id = typeof r.chartOfAccount === 'object'
          ? (r.chartOfAccount?._id?.toString() || r.chartOfAccount?.toString())
          : r.chartOfAccount?.toString();
        return id === recordsAccountId;
      });
    }
    if (recordsDateFrom) arr = arr.filter((r) => r.transactionDate && r.transactionDate >= recordsDateFrom);
    if (recordsDateTo)   arr = arr.filter((r) => r.transactionDate && r.transactionDate <= recordsDateTo + 'T23:59:59');
    return arr;
  }, [filteredByPeriod, allYearIncomeRecords, debouncedSearch, recordsAccountId, recordsDateFrom, recordsDateTo]);

  const recordsDedFiltered = useMemo(() => {
    let arr = filteredByPeriod(allYearDeductionRecords).sort(byDateDesc);
    if (debouncedSearch) {
      const s = debouncedSearch.toLowerCase();
      arr = arr.filter((r) => (r.description || '').toLowerCase().includes(s));
    }
    if (recordsAccountId) {
      arr = arr.filter((r) => {
        const id = typeof r.chartOfAccount === 'object'
          ? (r.chartOfAccount?._id?.toString() || r.chartOfAccount?.toString())
          : r.chartOfAccount?.toString();
        return id === recordsAccountId;
      });
    }
    if (recordsDateFrom) arr = arr.filter((r) => r.transactionDate && r.transactionDate >= recordsDateFrom);
    if (recordsDateTo)   arr = arr.filter((r) => r.transactionDate && r.transactionDate <= recordsDateTo + 'T23:59:59');
    return arr;
  }, [filteredByPeriod, allYearDeductionRecords, debouncedSearch, recordsAccountId, recordsDateFrom, recordsDateTo]);

  const records2307Filtered = useMemo(() => {
    let arr = filteredByPeriod(form2307Records).sort(byDateDesc);
    if (debouncedSearch) {
      const s = debouncedSearch.toLowerCase();
      arr = arr.filter((r) =>
        (r.description || '').toLowerCase().includes(s) ||
        (r.payerName || '').toLowerCase().includes(s) ||
        (r.vendor?.name || '').toLowerCase().includes(s)
      );
    }
    if (recordsDateFrom) arr = arr.filter((r) => r.transactionDate && r.transactionDate >= recordsDateFrom);
    if (recordsDateTo)   arr = arr.filter((r) => r.transactionDate && r.transactionDate <= recordsDateTo + 'T23:59:59');
    return arr;
  }, [filteredByPeriod, form2307Records, debouncedSearch, recordsDateFrom, recordsDateTo]);

  const recordsIncPaged  = useMemo(() => recordsIncFiltered.slice((recordsIncPage - 1) * RECORDS_LIMIT, recordsIncPage * RECORDS_LIMIT), [recordsIncFiltered, recordsIncPage]);
  const recordsDedPaged  = useMemo(() => recordsDedFiltered.slice((recordsDedPage - 1) * RECORDS_LIMIT, recordsDedPage * RECORDS_LIMIT), [recordsDedFiltered, recordsDedPage]);
  const records2307Paged = useMemo(() => records2307Filtered.slice((records2307Page - 1) * RECORDS_LIMIT, records2307Page * RECORDS_LIMIT), [records2307Filtered, records2307Page]);

  // ── Tabs list ─────────────────────────────────────────────────────────────
  const tabs = [
    { key: 'income', label: 'INCOME' },
    { key: 'deductions', label: 'DEDUCTIONS' },
    { key: 'form2307', label: 'FORM 2307' },
    { key: 'records', label: 'RECORDS' },
    { key: 'summary', label: 'SUMMARY' },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">
          Income Tax (BIR Form 1701 / 1702)
        </h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Manage ITR records and EWT credits for {client?.name}
        </p>
      </div>

      {/* Period selector */}
      <div className="bg-white border rounded-lg p-4">
        <PeriodSelector
          selectedPeriod={selectedPeriod}
          onChange={setSelectedPeriod}
          availableYears={availableYears}
        />
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-1">
          {tabs.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveSection(key)}
              className={`py-3 px-3 text-sm font-medium border-b-2 transition-colors ${
                activeSection === key
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {loading && <div className="p-4 text-sm text-gray-500">Loading…</div>}

      {/* ══ INCOME TAB ══════════════════════════════════════════════════════ */}
      {activeSection === 'income' && (
        <div className="space-y-4">
          {/* Encode form */}
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-base font-semibold text-teal-700 mb-4">Encode Income Record</h3>
            <form onSubmit={handleIncomeSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Month *</label>
                  <select
                    value={incomeForm.month}
                    onChange={(e) => setIncomeForm((p) => ({ ...p, month: e.target.value }))}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
                    required
                  >
                    {MONTH_NAMES.slice(1).map((name, idx) => (
                      <option key={idx + 1} value={idx + 1}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Income Type</label>
                  <select
                    value={incomeForm.incomeType}
                    onChange={(e) => setIncomeForm((p) => ({ ...p, incomeType: e.target.value }))}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
                  >
                    {INCOME_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Transaction Date</label>
                  <input
                    type="date"
                    value={incomeForm.transactionDate}
                    onChange={(e) => {
                      const val = e.target.value;
                      setIncomeForm((p) => ({ ...p, transactionDate: val }));
                      if (val) {
                        const d = new Date(val);
                        const txYear = isNaN(d.getTime()) ? null : getYear(d);
                        setIncomeDateError(
                          txYear !== null && txYear !== selectedPeriod.year
                            ? `Transaction Date year (${txYear}) does not match the selected Fiscal Year (${selectedPeriod.year}). Please correct one or the other.`
                            : ''
                        );
                      }
                    }}
                    className={`w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                      incomeDateError ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500'
                    }`}
                  />
                  {incomeDateError && (
                    <div className="flex items-start gap-1 mt-1">
                      <AlertCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-600">{incomeDateError}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description / Nature of Income *</label>
                  <ChartOfAccountsSearch
                    key={incomeFormKey}
                    value={incomeForm.chartOfAccountName || incomeForm.description}
                    onChange={(v) => setIncomeForm((p) => ({ ...p, description: v }))}
                    onSelect={(acc) => setIncomeForm((p) => ({
                      ...p,
                      chartOfAccount: acc?._id || null,
                      chartOfAccountName: acc ? `${acc.accountNumber} - ${acc.accountName}` : '',
                      description: acc ? acc.accountName : p.description,
                    }))}
                    placeholder="Search and select a Chart of Accounts entry"
                    showLinkedBadge={false}
                    clientId={client?._id}
                  />
                  {incomeForm.description && !incomeForm.chartOfAccount && (
                    <div className="flex items-start gap-1 mt-1">
                      <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-yellow-700">Description must be linked to a COA entry — select from the dropdown.</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Gross Amount (₱) *</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-3 flex items-center text-gray-500 text-sm">₱</span>
                    <input
                      type="number" step="0.01" min="0"
                      value={incomeForm.grossAmount}
                      onChange={(e) => setIncomeForm((p) => ({ ...p, grossAmount: e.target.value }))}
                      className="pl-7 w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    OR Number <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={incomeForm.orNumber}
                    onChange={(e) => setIncomeForm((p) => ({ ...p, orNumber: e.target.value }))}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                    placeholder="Official Receipt Number"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit" disabled={submittingIncome}
                  className="px-5 py-2 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:opacity-50"
                >
                  {submittingIncome ? 'Saving…' : 'Save Income Record'}
                </button>
              </div>
            </form>
          </div>

          {/* Recent income table */}
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-gray-700">Recent Income Records</h4>
                {recentIncomeTotal > incomeFiltered.length && (
                  <span className="text-xs text-gray-400">(showing latest {incomeFiltered.length} of {recentIncomeTotal})</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setActiveSection('records')}
                className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 font-medium"
              >
                View all records
                <ArrowRight className="h-3 w-3" />
              </button>
            </div>
            {incomeFiltered.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">No income records for selected period.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <Th>Month</Th><Th>Description</Th><Th>COA</Th>
                      <Th>Type</Th><Th>OR #</Th><Th right>Gross Amount</Th><Th>Date</Th><Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {incomeFiltered.map((r) => (
                      <tr key={r._id} className="hover:bg-gray-50">
                        <Td>{MONTH_NAMES[r.month] || r.month}</Td>
                        <Td>{r.description}</Td>
                        <Td mono>{r.chartOfAccountName || '—'}</Td>
                        <Td>{r.incomeType || '—'}</Td>
                        <Td mono>{r.orNumber || '—'}</Td>
                        <Td right>{fmt(r.grossAmount)}</Td>
                        <Td>{r.transactionDate ? new Date(r.transactionDate).toLocaleDateString('en-PH') : '—'}</Td>
                        <Td>
                          <div className="flex gap-2">
                            <EditBtn onClick={() => setEditingIncome(r)} />
                            <DeleteBtn onClick={() => handleDeleteIncome(r._id)} disabled={deletingId === r._id} />
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ DEDUCTIONS TAB ══════════════════════════════════════════════════ */}
      {activeSection === 'deductions' && (
        <div className="space-y-4">
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-base font-semibold text-teal-700 mb-4">Encode Deduction Record</h3>
            <form onSubmit={handleDeductionSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Month *</label>
                  <select
                    value={deductionForm.month}
                    onChange={(e) => setDeductionForm((p) => ({ ...p, month: e.target.value }))}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
                    required
                  >
                    {MONTH_NAMES.slice(1).map((name, idx) => (
                      <option key={idx + 1} value={idx + 1}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Deduction Type</label>
                  <select
                    value={deductionForm.deductionType}
                    onChange={(e) => setDeductionForm((p) => ({ ...p, deductionType: e.target.value, cosComponent: '' }))}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
                  >
                    {DEDUCTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Transaction Date</label>
                  <input
                    type="date"
                    value={deductionForm.transactionDate}
                    onChange={(e) => {
                      const val = e.target.value;
                      setDeductionForm((p) => ({ ...p, transactionDate: val }));
                      if (val) {
                        const d = new Date(val);
                        const txYear = isNaN(d.getTime()) ? null : getYear(d);
                        setDeductionDateError(
                          txYear !== null && txYear !== selectedPeriod.year
                            ? `Transaction Date year (${txYear}) does not match the selected Fiscal Year (${selectedPeriod.year}). Please correct one or the other.`
                            : ''
                        );
                      }
                    }}
                    className={`w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                      deductionDateError ? 'border-red-400 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500'
                    }`}
                  />
                  {deductionDateError && (
                    <div className="flex items-start gap-1 mt-1">
                      <AlertCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-600">{deductionDateError}</p>
                    </div>
                  )}
                </div>
              </div>
              {deductionForm.deductionType === 'Cost of Sales' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Cost of Sales Component *</label>
                  <select
                    value={deductionForm.cosComponent}
                    onChange={(e) => setDeductionForm((p) => ({ ...p, cosComponent: e.target.value }))}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
                    required
                  >
                    <option value="">Select component</option>
                    {COS_COMPONENTS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description *</label>
                  <ChartOfAccountsSearch
                    key={deductionFormKey}
                    value={deductionForm.chartOfAccountName || deductionForm.description}
                    onChange={(v) => setDeductionForm((p) => ({ ...p, description: v }))}
                    onSelect={(acc) => setDeductionForm((p) => ({
                      ...p,
                      chartOfAccount: acc?._id || null,
                      chartOfAccountName: acc ? `${acc.accountNumber} - ${acc.accountName}` : '',
                      description: acc ? acc.accountName : p.description,
                    }))}
                    placeholder="Search and select a Chart of Accounts entry"
                    showLinkedBadge={false}
                    clientId={client?._id}
                  />
                  {deductionForm.description && !deductionForm.chartOfAccount && (
                    <div className="flex items-start gap-1 mt-1">
                      <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-yellow-700">Description must be linked to a COA entry — select from the dropdown.</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Amount (₱) *</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-3 flex items-center text-gray-500 text-sm">₱</span>
                    <input
                      type="number" step="0.01" min="0"
                      value={deductionForm.amount}
                      onChange={(e) => setDeductionForm((p) => ({ ...p, amount: e.target.value }))}
                      className="pl-7 w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    OR Number <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={deductionForm.orNumber}
                    onChange={(e) => setDeductionForm((p) => ({ ...p, orNumber: e.target.value }))}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                    placeholder="Official Receipt Number"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit" disabled={submittingDeduction}
                  className="px-5 py-2 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:opacity-50"
                >
                  {submittingDeduction ? 'Saving…' : 'Save Deduction Record'}
                </button>
              </div>
            </form>
          </div>

          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-gray-700">Recent Deduction Records</h4>
                {recentDeductionTotal > deductionFiltered.length && (
                  <span className="text-xs text-gray-400">(showing latest {deductionFiltered.length} of {recentDeductionTotal})</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setActiveSection('records')}
                className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 font-medium"
              >
                View all records
                <ArrowRight className="h-3 w-3" />
              </button>
            </div>
            {deductionFiltered.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">No deduction records for selected period.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <Th>Month</Th><Th>Description</Th><Th>COA</Th>
                      <Th>Type</Th><Th>COGS Comp.</Th><Th>OR #</Th><Th right>Amount</Th><Th>Date</Th><Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {deductionFiltered.map((r) => (
                      <tr key={r._id} className="hover:bg-gray-50">
                        <Td>{MONTH_NAMES[r.month] || r.month}</Td>
                        <Td>{r.description}</Td>
                        <Td mono>{r.chartOfAccountName || '—'}</Td>
                        <Td>{r.deductionType || '—'}</Td>
                        <Td>{r.deductionType === 'Cost of Sales' ? (COS_COMPONENTS.find(c => c.value === r.cosComponent)?.label || '—') : '—'}</Td>
                        <Td mono>{r.orNumber || '—'}</Td>
                        <Td right>{fmt(r.amount)}</Td>
                        <Td>{r.transactionDate ? new Date(r.transactionDate).toLocaleDateString('en-PH') : '—'}</Td>
                        <Td>
                          <div className="flex gap-2">
                            <EditBtn onClick={() => setEditingDeduction(r)} />
                            <DeleteBtn onClick={() => handleDeleteDeduction(r._id)} disabled={deletingId === r._id} />
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ FORM 2307 TAB ═══════════════════════════════════════════════════ */}
      {activeSection === 'form2307' && (
        <div className="space-y-4">
          <div className="bg-white border rounded-lg">
            <Form2307Form
              client={client}
              onRecordAdded={fetchForm2307Records}
              selectedQuarter={selectedPeriod.quarter}
              selectedYear={selectedPeriod.year}
              selectedMonth={selectedPeriod.month}
            />
          </div>

          {/* Recent 2307 records */}
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50 flex justify-between items-center">
              <h4 className="text-sm font-semibold text-gray-700">Recent Form 2307 (EWT) Records</h4>
              <button
                type="button"
                onClick={() => setActiveSection('records')}
                className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 font-medium"
              >
                View all records
                <ArrowRight className="h-3 w-3" />
              </button>
            </div>
            {form2307Filtered.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">No EWT records for selected period.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <Th>Month</Th><Th>{term.one}</Th><Th>ATC</Th>
                      <Th right>Base Amount</Th><Th>Rate</Th><Th right>Tax Withheld</Th><Th>Date</Th><Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {form2307Filtered.map((r) => (
                      <tr key={r._id} className="hover:bg-gray-50">
                        <Td>{MONTH_NAMES[r.month] || r.month}</Td>
                        <Td>{r.vendor?.name || r.payerName || '—'}</Td>
                        <Td mono>{r.atcCode || '—'}</Td>
                        <Td right>{fmt(r.baseAmount)}</Td>
                        <Td>{r.withholdingRate}%</Td>
                        <Td right>{fmt(r.taxWithheld)}</Td>
                        <Td>{r.transactionDate ? new Date(r.transactionDate).toLocaleDateString('en-PH') : '—'}</Td>
                        <Td>
                          <div className="flex gap-2">
                            <EditBtn onClick={() => setEditingForm2307(r)} />
                            <DeleteBtn onClick={() => handleDeleteForm2307(r._id)} disabled={deletingId === r._id} />
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ RECORDS TAB ═════════════════════════════════════════════════════ */}
      {activeSection === 'records' && (
        <div className="space-y-4">
          {/* Summary strip */}
          <div className="bg-teal-50 border border-teal-100 rounded-lg p-4">
            <div className="flex flex-wrap gap-4 text-sm">
              <span>Income Records: <strong>{summaryData.incomeCount}</strong></span>
              <span>|</span>
              <span>Deductions: <strong>{summaryData.deductionCount}</strong></span>
              <span>|</span>
              <span>2307 Credits: <strong>{summaryData.form2307Count}</strong></span>
              <span className="ml-auto flex flex-wrap gap-4">
                <span>Total Income: <strong>{fmt(summaryData.totalIncome)}</strong></span>
                <span>COGS: <strong>{fmt(summaryData.cogs)}</strong></span>
                <span>Allowable Ded.: <strong>{fmt(summaryData.allowableDeductions)}</strong></span>
                <span className="font-semibold text-teal-800">
                  Net ITR Payable: {fmt(summaryData.netITRPayable)}
                </span>
              </span>
            </div>
          </div>

          {/* Search / filter controls */}
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                value={recordsSearch}
                onChange={(e) => setRecordsSearch(e.target.value)}
                placeholder="Search by description…"
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
            <div className="min-w-[160px]">
              <select
                value={recordsTypeFilter}
                onChange={(e) => setRecordsTypeFilter(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
              >
                <option value="all">All types</option>
                <option value="income">Income</option>
                <option value="deduction">Deduction</option>
                <option value="form2307">Form 2307</option>
              </select>
            </div>
            <div className="min-w-[220px]">
              <select
                value={recordsAccountId}
                onChange={(e) => setRecordsAccountId(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
              >
                <option value="">Filter by Account (all)</option>
                {recordsAccountOptions.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                value={recordsDateFrom}
                onChange={(e) => setRecordsDateFrom(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                title="Date from"
              />
              <span className="text-xs text-gray-400">—</span>
              <input
                type="date"
                value={recordsDateTo}
                onChange={(e) => setRecordsDateTo(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                title="Date to"
              />
            </div>
            {(recordsSearch || recordsAccountId || recordsTypeFilter !== 'all' || recordsDateFrom || recordsDateTo) && (
              <button
                type="button"
                onClick={() => { setRecordsSearch(''); setRecordsAccountId(''); setRecordsTypeFilter('all'); setRecordsDateFrom(''); setRecordsDateTo(''); }}
                className="px-3 py-1.5 text-xs border border-gray-300 rounded text-gray-600 hover:bg-gray-50"
              >
                Clear filters
              </button>
            )}
          </div>

          {/* DAT export */}
          <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200">
            <span className="text-xs text-gray-500 self-center">
              Export BIR DAT:
            </span>

            <button
              onClick={exportDAT}
              disabled={!form2307Records?.length}
              className="px-3 py-1.5 text-xs bg-red-700 text-white rounded hover:bg-red-800 disabled:opacity-40"
            >
              {client?.clientType === 'corporation' ? '1702' : '2551M'} Monthly
            </button>

            <button
              onClick={exportDAT}
              disabled={!form2307Records?.length}
              className="px-3 py-1.5 text-xs bg-red-800 text-white rounded hover:bg-red-900 disabled:opacity-40"
            >
              {client?.clientType === 'corporation' ? '1702Q' : '1701Q'} Quarterly
            </button>

            <span className="text-xs text-gray-500 self-center ml-2 pl-2 border-l border-gray-200">
              Summary:
            </span>

            <button
              onClick={exportSawtXls}
              disabled={!form2307Records?.length || !selectedPeriod.month}
              title={
                selectedPeriod.month
                  ? 'SAWT monthly summary as a spreadsheet'
                  : 'Select a month — this summary is a monthly report'
              }
              className="px-3 py-1.5 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-40"
            >
              SAWT Monthly XLS
            </button>

          </div>

          {/* Income sub-table */}
          {(recordsTypeFilter === 'all' || recordsTypeFilter === 'income') && (
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="px-4 py-2 border-b bg-green-50 flex justify-between items-center">
                <h4 className="text-sm font-semibold text-green-800">Income Records</h4>
                <span className="text-xs text-gray-500">{recordsIncFiltered.length} record(s)</span>
              </div>
              {recordsIncFiltered.length === 0 ? (
                <p className="p-4 text-sm text-gray-500">None for selected period.</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <Th>Month</Th><Th>Description</Th><Th>Income Type</Th>
                          <Th>OR #</Th><Th right>Gross Amount</Th><Th>Date</Th><Th>Actions</Th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {recordsIncPaged.map((r) => (
                          <tr key={r._id} className="hover:bg-gray-50">
                            <Td>{MONTH_NAMES[r.month] || r.month}</Td>
                            <Td>
                              <div className="flex items-center gap-1.5">
                                <span>{r.description}</span>
                                {r.chartOfAccountName ? (
                                  <span title={r.chartOfAccountName} className="inline-block w-2 h-2 rounded-full bg-teal-400 flex-shrink-0" />
                                ) : (
                                  <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 flex-shrink-0" title="Not linked to a COA entry" />
                                )}
                              </div>
                            </Td>
                            <Td>{r.incomeType || '—'}</Td>
                            <Td mono>{r.orNumber || '—'}</Td>
                            <Td right>{fmt(r.grossAmount)}</Td>
                            <Td>{r.transactionDate ? new Date(r.transactionDate).toLocaleDateString('en-PH') : '—'}</Td>
                            <Td>
                              <div className="flex gap-2">
                                <EditBtn onClick={() => setEditingIncome(r)} />
                                <DeleteBtn onClick={() => handleDeleteIncome(r._id)} disabled={deletingId === r._id} />
                              </div>
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <PaginationBar page={recordsIncPage} totalItems={recordsIncFiltered.length} limit={RECORDS_LIMIT} onPage={setRecordsIncPage} />
                </>
              )}
            </div>
          )}

          {/* Deductions sub-table */}
          {(recordsTypeFilter === 'all' || recordsTypeFilter === 'deduction') && (
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="px-4 py-2 border-b bg-blue-50 flex justify-between items-center">
                <h4 className="text-sm font-semibold text-blue-800">Deduction Records</h4>
                <span className="text-xs text-gray-500">{recordsDedFiltered.length} record(s)</span>
              </div>
              {recordsDedFiltered.length === 0 ? (
                <p className="p-4 text-sm text-gray-500">None for selected period.</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <Th>Month</Th><Th>Description</Th><Th>Deduction Type</Th>
                          <Th>COGS Comp.</Th><Th>OR #</Th><Th right>Amount</Th><Th>Date</Th><Th>Actions</Th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {recordsDedPaged.map((r) => (
                          <tr key={r._id} className="hover:bg-gray-50">
                            <Td>{MONTH_NAMES[r.month] || r.month}</Td>
                            <Td>
                              <div className="flex items-center gap-1.5">
                                <span>{r.description}</span>
                                {r.chartOfAccountName ? (
                                  <span title={r.chartOfAccountName} className="inline-block w-2 h-2 rounded-full bg-teal-400 flex-shrink-0" />
                                ) : (
                                  <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 flex-shrink-0" title="Not linked to a COA entry" />
                                )}
                              </div>
                            </Td>
                            <Td>{r.deductionType || '—'}</Td>
                            <Td>{r.deductionType === 'Cost of Sales' ? (COS_COMPONENTS.find((c) => c.value === r.cosComponent)?.label || '—') : '—'}</Td>
                            <Td mono>{r.orNumber || '—'}</Td>
                            <Td right>{fmt(r.amount)}</Td>
                            <Td>{r.transactionDate ? new Date(r.transactionDate).toLocaleDateString('en-PH') : '—'}</Td>
                            <Td>
                              <div className="flex gap-2">
                                <EditBtn onClick={() => setEditingDeduction(r)} />
                                <DeleteBtn onClick={() => handleDeleteDeduction(r._id)} disabled={deletingId === r._id} />
                              </div>
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <PaginationBar page={recordsDedPage} totalItems={recordsDedFiltered.length} limit={RECORDS_LIMIT} onPage={setRecordsDedPage} />
                </>
              )}
            </div>
          )}

          {/* 2307 sub-table */}
          {(recordsTypeFilter === 'all' || recordsTypeFilter === 'form2307') && (
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="px-4 py-2 border-b bg-purple-50 flex justify-between items-center">
                <h4 className="text-sm font-semibold text-purple-800">Form 2307 (EWT Credits)</h4>
                <span className="text-xs text-gray-500">{records2307Filtered.length} record(s)</span>
              </div>
              {records2307Filtered.length === 0 ? (
                <p className="p-4 text-sm text-gray-500">None for selected period.</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <Th>Month</Th><Th>{term.one}</Th><Th>ATC</Th>
                          <Th right>Base Amount</Th><Th>Rate %</Th><Th right>Tax Withheld</Th><Th>Actions</Th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {records2307Paged.map((r) => (
                          <tr key={r._id} className="hover:bg-gray-50">
                            <Td>{MONTH_NAMES[r.month] || r.month}</Td>
                            <Td>{r.vendor?.name || r.payerName || '—'}</Td>
                            <Td mono>{r.atcCode || '—'}</Td>
                            <Td right>{fmt(r.baseAmount)}</Td>
                            <Td>{r.withholdingRate}%</Td>
                            <Td right>{fmt(r.taxWithheld)}</Td>
                            <Td>
                              <div className="flex gap-2">
                                <EditBtn onClick={() => setEditingForm2307(r)} />
                                <DeleteBtn onClick={() => handleDeleteForm2307(r._id)} disabled={deletingId === r._id} />
                              </div>
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <PaginationBar page={records2307Page} totalItems={records2307Filtered.length} limit={RECORDS_LIMIT} onPage={setRecords2307Page} />
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ══ SUMMARY TAB ═════════════════════════════════════════════════════ */}
      {activeSection === 'summary' && (
        <div className="space-y-4">

          {/* ── Corp: 4-card grid ── */}
          {isCorpClient ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                <p className="text-sm text-green-800 font-medium">Total Income</p>
                <p className="text-2xl font-bold text-green-900 mt-1">₱{fmtAmount(summaryData.totalIncome)}</p>
                <p className="text-xs text-green-600 mt-1">{summaryData.incomeCount} record(s)</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-100">
                <p className="text-sm text-orange-800 font-medium">Cost of Sales (COGS)</p>
                <p className="text-2xl font-bold text-orange-900 mt-1">₱{fmtAmount(summaryData.cogs)}</p>
                <p className="text-xs text-orange-600 mt-1">Beg. Inventory + Purchases − End. Inventory</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <p className="text-sm text-blue-800 font-medium">Gross Income</p>
                <p className="text-2xl font-bold text-blue-900 mt-1">₱{fmtAmount(summaryData.grossIncome)}</p>
                <p className="text-xs text-blue-600 mt-1">Total Income − COGS</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                <p className="text-sm text-indigo-800 font-medium">Allowable Deductions</p>
                <p className="text-2xl font-bold text-indigo-900 mt-1">₱{fmtAmount(summaryData.allowableDeductions)}</p>
                <p className="text-xs text-indigo-600 mt-1">Excluding COGS</p>
              </div>
            </div>
          ) : (
            /* ── Individual: same 4-card chain as Corp ── */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                <p className="text-sm text-green-800 font-medium">Total Income</p>
                <p className="text-2xl font-bold text-green-900 mt-1">₱{fmtAmount(summaryData.totalIncome)}</p>
                <p className="text-xs text-green-600 mt-1">{summaryData.incomeCount} record(s)</p>
              </div>
              <div className={`p-4 rounded-lg border ${itrMethod === '8% Flat Rate' ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-orange-50 border-orange-100'}`}>
                <p className="text-sm text-orange-800 font-medium">Cost of Sales (COGS)</p>
                <p className="text-2xl font-bold text-orange-900 mt-1">₱{fmtAmount(summaryData.cogs)}</p>
                <p className="text-xs text-orange-600 mt-1">{itrMethod === '8% Flat Rate' ? 'Not used under 8% method' : 'Beg. + Purchases − End. Inventory'}</p>
              </div>
              <div className={`p-4 rounded-lg border ${itrMethod === '8% Flat Rate' ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-blue-50 border-blue-100'}`}>
                <p className="text-sm text-blue-800 font-medium">Gross Income</p>
                <p className="text-2xl font-bold text-blue-900 mt-1">₱{fmtAmount(summaryData.grossIncome)}</p>
                <p className="text-xs text-blue-600 mt-1">{itrMethod === '8% Flat Rate' ? 'Not used under 8% method' : 'Total Income − COGS'}</p>
              </div>
              <div className={`p-4 rounded-lg border ${itrMethod === '8% Flat Rate' ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-indigo-50 border-indigo-100'}`}>
                <p className="text-sm text-indigo-800 font-medium">Allowable Deductions</p>
                <p className="text-2xl font-bold text-indigo-900 mt-1">₱{fmtAmount(summaryData.allowableDeductions)}</p>
                <p className="text-xs text-indigo-600 mt-1">{itrMethod === '8% Flat Rate' ? 'Not used under 8% method' : 'Excluding COGS'}</p>
              </div>
            </div>
          )}

          {/* Taxable Income */}
          <div className={`p-4 rounded-lg border ${!isCorpClient && itrMethod === '8% Flat Rate' ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-amber-50 border-amber-100'}`}>
            <p className="text-sm text-amber-800 font-medium">Taxable Income</p>
            <p className="text-3xl font-bold text-amber-900 mt-1">₱{fmtAmount(summaryData.taxableIncome)}</p>
            <p className="text-xs text-amber-700 mt-1">
              {!isCorpClient && itrMethod === '8% Flat Rate'
                ? 'Not used under 8% method — tax computed on gross less ₱250,000'
                : 'Gross Income − Allowable Deductions'}
            </p>
          </div>

          {/* ITR Tax Due */}
          {isCorpClient ? (
            /* Corp: RIT/MCIT computation */
            <div className="space-y-3 bg-yellow-50 p-5 rounded-lg border border-yellow-100">
              <p className="text-sm text-yellow-800 font-semibold">ITR Tax Computation (Form 1702)</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">RIT Rate</label>
                  <select
                    value={ritRate}
                    onChange={(e) => setRitRate(parseFloat(e.target.value))}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
                  >
                    <option value={0.20}>20%</option>
                    <option value={0.25}>25% (default)</option>
                    <option value={0.30}>30%</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={subjectToMCIT}
                      onChange={(e) => setSubjectToMCIT(e.target.checked)}
                      className="rounded border-gray-300 focus:ring-teal-500"
                    />
                    <span className="text-gray-700">Subject to MCIT? (&gt;4 years old)</span>
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 bg-white rounded p-3 border border-yellow-200">
                <div>
                  <p className="text-xs text-gray-600 mb-1">RIT (Regular)</p>
                  <p className="text-lg font-bold text-yellow-900">₱{fmtAmount(computedRIT)}</p>
                  <p className="text-xs text-gray-500">@{(ritRate * 100).toFixed(0)}% × Taxable Income</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">MCIT (Minimum)</p>
                  <p className="text-lg font-bold text-yellow-900">₱{fmtAmount(computedMCIT)}</p>
                  <p className="text-xs text-gray-500">@2% × Gross Income</p>
                </div>
              </div>
              <div className="bg-white rounded p-3 border border-yellow-200">
                <p className="text-xs text-gray-600 mb-1">ITR Tax Due (Higher of RIT / MCIT)</p>
                <p className="text-2xl font-bold text-yellow-900">₱{fmtAmount(itrTaxDue)}</p>
                <p className="text-xs text-yellow-700 mt-1">
                  {computedRIT > computedMCIT ? 'RIT applied' : computedMCIT > 0 ? 'MCIT applied — minimum tax exceeds regular tax' : 'Not yet computed'}
                </p>
              </div>
              <button
                type="button"
                onClick={saveItrTaxDue}
                disabled={savingTaxDue || !selectedPeriod.quarter}
                className="w-full px-4 py-2 bg-teal-600 text-white rounded text-sm hover:bg-teal-700 disabled:opacity-50"
              >
                {savingTaxDue ? 'Computing…' : 'Compute & Save ITR Tax Due'}
              </button>
            </div>
          ) : (
            /* Individual: computed ITR Tax Due — graduated table or 8% flat */
            <div className="space-y-3 bg-yellow-50 p-5 rounded-lg border border-yellow-100">
              <p className="text-sm text-yellow-800 font-semibold">ITR Tax Computation (Form 1701)</p>

              {/* Method selector */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Income Tax Method</label>
                <select
                  value={itrMethod}
                  onChange={(e) => setItrMethod(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
                >
                  <option value="Graduated (Itemized)">Graduated (Itemized)</option>
                  <option value="8% Flat Rate">8% Flat Rate</option>
                </select>
              </div>

              {/* 8% eligibility warning */}
              {itrMethod === '8% Flat Rate' && summaryData.totalIncome > 3000000 && (
                <div className="bg-orange-50 border border-orange-200 rounded p-2">
                  <p className="text-xs text-orange-700">
                    ⚠️ 8% option not available above ₱3M gross — verify eligibility.
                  </p>
                </div>
              )}

              {/* Computation breakdown */}
              <div className="bg-white rounded p-3 border border-yellow-200">
                {itrMethod === '8% Flat Rate' ? (
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Tax Base: Gross Sales − ₱250,000 exemption</p>
                    <p className="text-sm text-gray-700">₱{fmtAmount(Math.max(0, summaryData.totalIncome - 250000))} × 8%</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Graduated Tax on Taxable Income</p>
                    <p className="text-sm text-gray-700">₱{fmtAmount(summaryData.taxableIncome)} → BIR TRAIN table</p>
                  </div>
                )}
                <p className="text-2xl font-bold text-yellow-900 mt-2">
                  ₱{fmtAmount(summaryData.computedIndivTaxDue)}
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  {itrMethod === '8% Flat Rate' ? '8% flat rate (gross less ₱250,000)' : 'Graduated tax (TRAIN law)'}
                </p>
              </div>

              <button
                type="button"
                onClick={saveItrTaxDue}
                disabled={savingTaxDue || !selectedPeriod.quarter}
                className="w-full px-4 py-2 bg-teal-600 text-white rounded text-sm hover:bg-teal-700 disabled:opacity-50"
              >
                {savingTaxDue ? 'Computing…' : 'Compute & Save ITR Tax Due'}
              </button>
              {!selectedPeriod.quarter && (
                <p className="text-xs text-yellow-700">Select a quarter to save ITR Tax Due.</p>
              )}
            </div>
          )}

          {/* EWT Credits */}
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
            <p className="text-sm text-purple-800 font-medium">Form 2307 (EWT Credits)</p>
            <p className="text-2xl font-bold text-purple-900 mt-1">₱{fmtAmount(summaryData.total2307Credits)}</p>
            <p className="text-xs text-purple-600 mt-1">{summaryData.form2307Count} certificate(s)</p>
          </div>

          {/* Net ITR Payable */}
          <div className={`p-5 rounded-lg border ${summaryData.excessCredit > 0 ? 'bg-amber-50 border-amber-200' : 'bg-teal-50 border-teal-200'}`}>
            <p className="text-sm font-semibold text-gray-700">Net ITR Payable</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">₱{fmtAmount(summaryData.netITRPayable)}</p>
            {summaryData.excessCredit > 0 && (
              <p className="text-xs text-amber-700 mt-2">
                Excess EWT Credit: ₱{fmtAmount(summaryData.excessCredit)} — carry forward to next year
              </p>
            )}
          </div>

          {/* Q2/Q3/Q4: Cumulative carry-over */}
          {cumulativeData && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
              <h4 className="text-sm font-semibold text-slate-800 mb-3">
                Cumulative ITR (Q1 – Q{selectedPeriod.quarter}){' '}
                <span className="font-normal text-slate-500 text-xs">
                  — {isCorpClient ? 'Form 1702' : itrMethod === '8% Flat Rate' ? '8% Flat Rate' : 'Graduated Table applied on cumulative taxable income'}
                </span>
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div><p className="text-xs text-gray-500">Cumulative Income</p><p className="font-semibold">{fmt(cumulativeData.cumTotalIncome)}</p></div>
                <div><p className="text-xs text-gray-500">Cumulative COGS</p><p className="font-semibold">{fmt(cumulativeData.cumCogs)}</p></div>
                <div><p className="text-xs text-gray-500">Cum. Gross Income</p><p className="font-semibold">{fmt(cumulativeData.cumGrossIncome)}</p></div>
                <div><p className="text-xs text-gray-500">Cum. Allowable Ded.</p><p className="font-semibold">{fmt(cumulativeData.cumAllowableDeductions)}</p></div>
                <div><p className="text-xs text-gray-500">Cum. Taxable Income</p><p className="font-semibold">{fmt(cumulativeData.cumTaxableIncome)}</p></div>
                <div><p className="text-xs text-gray-500">Cum. ITR Tax Due</p><p className="font-semibold">{fmt(cumulativeData.cumItrTaxDue)}</p></div>
                <div><p className="text-xs text-gray-500">Cum. EWT Credits</p><p className="font-semibold">{fmt(cumulativeData.cumEWTCredits)}</p></div>
                <div><p className="text-xs text-gray-500">Prior Qtrs Paid</p><p className="font-semibold">{fmt(cumulativeData.priorPaid)}</p></div>
                <div>
                  <p className="text-xs text-gray-500">Cum. Net ITR Payable</p>
                  <p className="text-lg font-bold text-teal-700">{fmt(cumulativeData.cumNetITRPayable)}</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3 font-mono">
                Cum. Net ITR = Cum. ITR Tax Due − Cum. EWT Credits − Prior Quarters Paid
              </p>
            </div>
          )}

          {/* Record Payment card */}
          {selectedPeriod.quarter && (
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-semibold text-gray-800">Record Payment</h4>
                {paymentStatus && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    paymentStatus.color === 'green' ? 'bg-green-100 text-green-800' :
                    paymentStatus.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                    paymentStatus.color === 'red' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {paymentStatus.label}
                    {paymentStatus.totalPaid > 0 && ` — ${fmt(paymentStatus.totalPaid)} paid`}
                  </span>
                )}
              </div>

              {itrPayments.length > 0 && (
                <div className="mb-4 overflow-x-auto">
                  <table className="min-w-full text-xs divide-y divide-gray-200 border rounded">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-500 font-medium">Date</th>
                        <th className="px-3 py-2 text-right text-gray-500 font-medium">Amount</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-medium">Reference</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-medium">Remarks</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {itrPayments.map((p) => (
                        <tr key={p._id}>
                          <td className="px-3 py-2">{new Date(p.paymentDate).toLocaleDateString('en-PH')}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmt(p.amountPaid)}</td>
                          <td className="px-3 py-2">{p.referenceNumber || '—'}</td>
                          <td className="px-3 py-2">{p.remarks || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!showPaymentForm ? (
                <button
                  type="button"
                  onClick={() => {
                    setPaymentForm((p) => ({ ...p, amountPaid: summaryData.netITRPayable > 0 ? summaryData.netITRPayable.toFixed(2) : '' }));
                    setShowPaymentForm(true);
                  }}
                  className="px-4 py-2 text-sm bg-teal-600 text-white rounded hover:bg-teal-700"
                >
                  + Record Payment
                </button>
              ) : (
                <form onSubmit={handleRecordPayment} className="space-y-3 border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Payment Date *</label>
                      <input
                        type="date"
                        value={paymentForm.paymentDate}
                        onChange={(e) => setPaymentForm((p) => ({ ...p, paymentDate: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Amount Paid (₱) *</label>
                      <input
                        type="number" step="0.01" min="0"
                        value={paymentForm.amountPaid}
                        onChange={(e) => setPaymentForm((p) => ({ ...p, amountPaid: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Reference No.</label>
                      <input
                        type="text"
                        value={paymentForm.referenceNumber}
                        onChange={(e) => setPaymentForm((p) => ({ ...p, referenceNumber: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                        placeholder="e.g. BIR receipt no."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
                      <input
                        type="text"
                        value={paymentForm.remarks}
                        onChange={(e) => setPaymentForm((p) => ({ ...p, remarks: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => setShowPaymentForm(false)}
                      className="px-4 py-1.5 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submittingPayment}
                      className="px-4 py-1.5 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
                    >
                      {submittingPayment ? 'Saving…' : 'Save Payment'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Formula */}
          <p className="text-xs text-gray-500 font-mono">
            Formula: Net ITR Payable = ITR Tax Due − Form 2307 EWT Credits
          </p>

          {/* Info banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 font-medium">
              Form 2307 (EWT) certificates received from customers/clients are applied as
              creditable tax deductions against your ITR Tax Due. They do NOT affect VAT or
              Percentage Tax payable.
            </p>
          </div>
        </div>
      )}

      {/* Edit modals */}
      {editingIncome && (
        <InlineEditModal
          type="income"
          record={editingIncome}
          fiscalYear={editingIncome.fiscalYear}
          isCorpClient={isCorpClient}
          clientId={editingIncome.accountingClient?.toString() || client?._id}
          onSave={async (id, data) => { await itrAPI.updateIncome(id, data); await fetchIncomeRecords(); if (isCorpClient) await fetchAllYearData(); }}
          onClose={() => setEditingIncome(null)}
        />
      )}
      {editingDeduction && (
        <InlineEditModal
          type="deduction"
          record={editingDeduction}
          isCorpClient={isCorpClient}
          clientId={editingDeduction.accountingClient?.toString() || client?._id}
          onSave={async (id, data) => { await itrAPI.updateDeduction(id, data); await fetchDeductionRecords(); if (isCorpClient) await fetchAllYearData(); }}
          onClose={() => setEditingDeduction(null)}
        />
      )}
      {editingForm2307 && (
        <Form2307EditModal
          record={editingForm2307}
          payeeClass={clientPayeeClass}
          onSave={async (id, data) => {
            await formAPI.updateForm2307Record(id, data);
            await fetchForm2307Records();
            await fetchAllYearData();
          }}
          onClose={() => setEditingForm2307(null)}
        />
      )}
    </div>
  );
};

export default ITRManagement;
